// Wepster messenger chat — shared by the client card ("Messages" tab) and
// /social-leads. Messages go out of OUR messenger accounts connected in
// Wepster (personal Telegram / Viber / WhatsApp Business), so a manager can
// write first to a client who never wrote to us.
import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { ExternalLink, Link2, Loader2, RefreshCw, Send, Paperclip } from "lucide-react";

export const MESSENGER_LABEL = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  viber: "Viber",
  alphasms: "Viber Business",
  echat: "eChat",
  messenger: "Messenger",
  facebook: "Messenger",
};

export function MessengerBadge({ type }) {
  if (!type) return null;
  return (
    <Badge variant="outline" className="text-[10px] border-sky-500/40 text-sky-400 bg-sky-500/10">
      {MESSENGER_LABEL[type] || type}
    </Badge>
  );
}

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const errText = (e) => e?.response?.data?.detail || e?.message || "Request failed";

export async function openInWepster(wepsterId) {
  try {
    const { data } = await api.post("/wepster/login-link", wepsterId ? { wepster_id: wepsterId } : {});
    if (data?.login_url) window.open(data.login_url, "_blank", "noopener");
  } catch (e) {
    toast.error(errText(e));
  }
}

export function MessageList({ messages, emptyText }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages?.length]);
  if (!messages?.length) {
    return <p className="text-sm text-zinc-500 py-8 text-center">{emptyText || "No messages yet"}</p>;
  }
  return (
    <div className="space-y-1.5">
      {messages.map((m) => {
        const out = m.direction === "out";
        return (
          <div key={m.id} className={cn("flex", out ? "justify-end" : "justify-start")}>
            <div
              className={cn(
                "max-w-[78%] rounded-xl px-3 py-1.5 text-sm whitespace-pre-wrap break-words",
                out ? "bg-sky-500/15 text-zinc-100" : "bg-zinc-800 text-zinc-200",
              )}
            >
              {m.text}
              {m.media?.kind && !m.media?.url && (
                <span className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                  <Paperclip className="h-3 w-3" />{m.media.kind}
                </span>
              )}
              {m.media?.url && (
                <a href={m.media.url} target="_blank" rel="noreferrer"
                   className="flex items-center gap-1 text-xs text-sky-400 hover:underline mt-1">
                  <Paperclip className="h-3 w-3" />{m.media.kind || "file"}
                </a>
              )}
              <div className="text-[10px] text-zinc-500 mt-0.5 text-right">
                {m.from === "bot-message" || m.from === "chain-message" ? "auto · " : ""}
                {fmtTime(m.created_at)}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

export function Composer({ onSend, disabled, placeholder }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await onSend(t);
      setText("");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex items-end gap-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
        }}
        disabled={disabled || sending}
        rows={2}
        placeholder={placeholder || "Message… (Enter — send, Shift+Enter — new line)"}
        className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm resize-none"
      />
      <Button onClick={send} disabled={disabled || sending || !text.trim()} className="bg-sky-600 hover:bg-sky-500 h-10">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </div>
  );
}

// Client card tab.
export default function ClientMessagesTab({ clientId, canWrite }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLead, setActiveLead] = useState(null); // wepster_id or null = all chats
  const [fromConn, setFromConn] = useState(""); // "" = continue the chat; id = start from that account

  const load = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/wepster`);
      setData(res.data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setActiveLead(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (loading) return <p className="text-zinc-500 py-8 text-center">Loading...</p>;
  if (!data) return null;
  if (!data.configured) {
    return (
      <p className="text-sm text-zinc-500 py-8 text-center">
        Wepster is not connected yet — an admin adds the API token on Settings → Wepster.
      </p>
    );
  }

  const leads = data.leads || [];
  const msgs = (data.messages || []).filter((m) => !activeLead || m.wepster_id === activeLead);
  const target = activeLead || leads[0]?.wepster_id || null;

  const link = async (wid) => {
    try {
      const res = await api.post(`/wepster/leads/${wid}/link`, { client_id: clientId });
      if (res.data?.warning) toast.warning(res.data.warning); else toast.success("Chat linked");
      await api.post(`/wepster/leads/${wid}/refresh`).catch(() => {});
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const send = async (text) => {
    const body = fromConn ? { text, connection_id: fromConn } : (target ? { text, wepster_id: target } : { text });
    await api.post(`/clients/${clientId}/wepster/send`, body);
    setFromConn("");
    load();
  };
  const myChannels = data.my_channels || [];

  const canSend = canWrite && (leads.length > 0 || data.can_start_by_phone);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {leads.length > 1 && (
          <button
            onClick={() => setActiveLead(null)}
            className={cn("text-xs px-2 py-0.5 rounded-md border",
              !activeLead ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}
          >
            All chats
          </button>
        )}
        {leads.map((l) => (
          <button
            key={l.wepster_id}
            onClick={() => setActiveLead(l.wepster_id)}
            className={cn("text-xs px-2 py-0.5 rounded-md border flex items-center gap-1",
              activeLead === l.wepster_id ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}
          >
            {MESSENGER_LABEL[l.messenger_type] || l.messenger_type || "chat"} · {l.name || l.phone || "—"}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-zinc-400" onClick={() => { setLoading(true); load(); }}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {target && (
            <Button size="sm" variant="ghost" className="h-7 text-zinc-400" onClick={() => openInWepster(target)}>
              <ExternalLink className="h-3.5 w-3.5 mr-1" />Wepster
            </Button>
          )}
        </div>
      </div>

      {data.suggestions?.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 space-y-1">
          <p className="text-xs text-amber-300">Chats with the same phone, not linked yet:</p>
          {data.suggestions.map((s) => (
            <div key={s.wepster_id} className="flex items-center gap-2 text-xs text-zinc-300">
              <MessengerBadge type={s.messenger_type} />
              <span>{s.name || "—"} · {s.phone}</span>
              {canWrite && (
                <Button size="sm" variant="ghost" className="h-6 ml-auto text-amber-300" onClick={() => link(s.wepster_id)}>
                  <Link2 className="h-3 w-3 mr-1" />Link
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="max-h-[380px] overflow-y-auto pr-1">
        <MessageList
          messages={msgs}
          emptyText={leads.length ? "No messages yet" : (data.client_phone
            ? `No chat yet. ${data.can_start_by_phone ? `The first message goes to +${data.client_phone} from your ${MESSENGER_LABEL[data.default_channel_type] || data.default_channel_type}.` : "You have no messenger account yet — ask an admin to assign one on Settings → Wepster."}`
            : "No chat linked and no phone on the client card.")}
        />
      </div>

      {canWrite && myChannels.length > 0 && (leads.length > 0 || myChannels.length > 1) && (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>From:</span>
          <select
            value={fromConn}
            onChange={(e) => setFromConn(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-7 rounded-md px-2 text-xs"
            data-testid="wepster-from-account"
          >
            {leads.length > 0
              ? <option value="">continue this chat{leads.find((l) => l.wepster_id === target)?.account_name ? ` (${leads.find((l) => l.wepster_id === target).account_name})` : ""}</option>
              : <option value="">{MESSENGER_LABEL[myChannels[0].type] || myChannels[0].type} · {myChannels[0].name}{myChannels[0].own ? " (mine)" : " (company)"}</option>}
            {myChannels.slice(leads.length > 0 ? 0 : 1).map((c) => (
              <option key={c.connection_id} value={c.connection_id}>
                new chat · {MESSENGER_LABEL[c.type] || c.type} · {c.name}{c.own ? " (mine)" : " (company)"}
              </option>
            ))}
          </select>
        </div>
      )}

      {canWrite && (
        <Composer
          onSend={send}
          disabled={!canSend}
          placeholder={canSend ? undefined : "Link a chat or add a phone to the client card"}
        />
      )}

      {data.outbox?.length > 0 && (
        <div className="pt-2 border-t border-zinc-800">
          <p className="text-[10px] uppercase text-zinc-500 mb-1">Automatic notifications</p>
          {data.outbox.slice(0, 5).map((o) => (
            <div key={o.id} className="flex items-center gap-2 text-xs text-zinc-400">
              <span className={o.status === "sent" ? "text-emerald-400" : "text-red-400"}>
                {o.status === "sent" ? "✓" : "✕"}
              </span>
              <span>{o.event}</span>
              <span className="text-zinc-600">{fmtTime(o.created_at)}</span>
              {o.error && <span className="text-red-400 truncate">{o.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
